import { Component, ViewChild, OnInit } from '@angular/core';
import { CradlepointsService } from '@app/features/cradlepoints/shared/cradlepoints.service';
import { LocationsService } from '@app/features/locations/shared/locations.service';
import { Location } from '@app/shared/models/location.model';
import { Cradlepoint } from '@app/shared/models/cradlepoint/cradlepoint.model';
import { CradlepointLocation } from '@app/shared/models/cradlepoint/cradlepoint-location.model';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { firstValueFrom } from 'rxjs';

@Component({
    selector: 'app-chain-wide-cradlepoints',
    templateUrl: './chain-wide-cradlepoints.component.html',
    styleUrls: ['./chain-wide-cradlepoints.component.css'],
    standalone: false
})
export class ChainWideCradlepointsComponent implements OnInit {
    @ViewChild('cradlepointTable') cradlepointTable: MatTable<Cradlepoint>;
    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
    @ViewChild(MatSort, { static: true }) sort: MatSort;
    @ViewChild(MatSlideToggleModule, { static: true }) slideTogle: MatSlideToggleModule;
    isLoading = false;
    onlyShowOffline: boolean;
    hideLabs: boolean;
    hideFutures: boolean;
    hideRemodels: boolean;
    filterText: string = null;
    locations = [];
    allCradlepoints = [];
    cradlepointLocations = []
    displayedColumns = ['storeNumber', 'locationName', 'locationTimeZone', 'locationStatus', 'cradlepoints'];
    dataSource = new MatTableDataSource<CradlepointLocation>(this.cradlepointLocations);

    constructor(private cradlepointService: CradlepointsService, private locationsService: LocationsService) { }

    ngOnInit() {
        this.initComponent();
    }

    private async initComponent() {
        this.isLoading = true;
        await this.createDataSource();
        this.dataSource.paginator = this.paginator;
        this.dataSource.sortingDataAccessor = (item, property) => {
            switch (property) {
                case 'locationName': return item.location.name;
                case 'locationTimeZone': return item.location.timezone;
                case 'locationStatus': return item.location.status;
                default: return item[property];
            }
        };
        this.dataSource.sort = this.sort;
        this.cradlepointTable.renderRows();
        this.isLoading = false;
    }

    async getAllCradlepoints() {
        try {
            const observable = await this.cradlepointService.getAllCradlepoints();
            const cradlepoints_from_response = await firstValueFrom(observable);
            if (typeof cradlepoints_from_response === 'string') {
                console.log("Cradlepoints timed out!");
                return;
            }
            Object.entries(cradlepoints_from_response["message"]).forEach(location_data => {
                let cradlepointLocation: CradlepointLocation = new CradlepointLocation;
                cradlepointLocation.cradlepoints = [];
                cradlepointLocation.storeNumber = location_data[0];
                for (let cradlepoint of location_data[1] as Cradlepoint[]) {
                    cradlepointLocation.cradlepoints.push(cradlepoint);
                }
                this.cradlepointLocations.push(cradlepointLocation);
            });
        } catch (error) {
            console.log(error);
        }
    }

    async getAllLocations() {
        try {
            const observable = await this.locationsService.getAllLocationData();
            const locations_from_response = await firstValueFrom(observable);
            if (typeof locations_from_response === 'string') {
                console.log("Locations timed out!");
                return;
            }
            locations_from_response["message"]["locationList"].forEach(locations_json => {
                let location: Location = new Location;
                location.name = locations_json["core"]["locationName"];
                location.number = locations_json["locationNumber"];
                location.status = locations_json["locationStatus"]["locationStatus"];

                if (!locations_json["core"].hasOwnProperty('timeZone')
                    || !locations_json["core"]["timeZone"].hasOwnProperty('timeZoneName')) {
                    location.timezone = "";
                } else {
                    location.timezone = locations_json["core"]["timeZone"]["timeZoneName"].split(" ")[2];
                }

                // Some locations don't have operators, such as locations with a 'future' state
                if (!locations_json.hasOwnProperty('operator')) {
                    location.operatorName = "";
                } else {
                    location.operatorName = locations_json["operator"]["name"];
                }
                this.locations.push(location);
            });
        } catch (error) {
            console.log(error);
        }
    }

    async createDataSource(locations?) {

        if (locations == null) {
            await this.getAllLocations();
        } else {
            this.locations = locations;
        }

        await this.getAllCradlepoints();

        let locationMap = new Map<string, Location>();
        this.locations.forEach(location => {
            locationMap.set(location.number, location);
        });

        for (let cradlepointLocation of this.cradlepointLocations) {
            if (locationMap.get(cradlepointLocation.storeNumber)) {
                cradlepointLocation.location = locationMap.get(cradlepointLocation.storeNumber);
            } else {
                const startingIndex = cradlepointLocation.cradlepoints[0].name.search('[0-9]{5}');
                const storeNumber = cradlepointLocation.cradlepoints[0].name.substring(startingIndex, startingIndex + 5);
                cradlepointLocation.location = {
                    'name': "",
                    'status': "",
                    'timezone': "",
                    'number': storeNumber
                }
            }
        }
    }

    onToggle() {
        this.applyFilter(this.filterText);
    }

    applyFilter(filterValue: string) {
        this.dataSource.filterPredicate = (data: CradlepointLocation, filter: string) => {
            let hasOfflineCradlepoints = false;
            let containsFilterValue = false;

            // Handle external access point filtering
            if (this.onlyShowOffline) {
                for (let cradlepoint of data.cradlepoints) {
                    if (cradlepoint.state == 'offline') {
                        hasOfflineCradlepoints = true;
                    }
                }
            }

            // Handle filter text filtering
            if (filter == "null"
                || data.location.number.toLowerCase().includes(filterValue)
                || data.location.name.toLowerCase().includes(filterValue)
                || data.location.timezone.toLowerCase().includes(filterValue)
                || data.location.status.toLowerCase().includes(filterValue)) {
                containsFilterValue = true;
            }

            // Coalesce and make filtering decision
            if (this.hideLabs && data.location.number.charAt(0) == "9") {
                return false;
            } else if (this.hideFutures && data.location.status == "future") {
                return false;
            } else if (this.hideRemodels && data.location.status == "remodel") {
                return false;
            } else if (!containsFilterValue) {
                return false
            } else if (this.onlyShowOffline && !hasOfflineCradlepoints) {
                return false;
            } else {
                return true;
            }
        }

        if (filterValue == null || filterValue == "") { // handles case where no filter text is provided
            this.dataSource.filter = "null";
        } else {
            this.dataSource.filter = filterValue.trim().toLowerCase();
        }
    }
}